/**
 * 🛟 **올린 파일은 남았는데 책이 사라진 것을 되살린다.**
 *
 * ## 무슨 일이 있었나
 * 운영에 파일 31개가 그 상태였다(2026-08-28 발견). 뜯어보니 **잡동사니가 아니었다**:
 * 표지 1 · 본문 10쪽 · **읽어주기 20개(한국어·영어 각 10)**. 읽어주기는 운영자가
 * 목소리를 골라 한 쪽씩 만드는 것이고 비용도 든다([[book-narration-dubbing]]).
 * 즉 **한 사람이 한나절 넘게 만든 책 한 권**이 목록에서만 사라진 것이다.
 *
 * 그런데 관리자가 할 수 있는 말은 「책을 다시 만들거나 파일을 지워 주세요」뿐이었다 —
 * 다시 만들려면 31개를 **다시 올려야** 한다. 그럴 바엔 아무도 안 한다.
 *
 * ## 무엇을 되살리나 (그리고 못 되살리나)
 * | 되살아난다 | 못 되살린다 |
 * |---|---|
 * | 쪽 순서·표지·그림·읽어주기(주소가 그대로다) | 자막·대체글·제목 |
 *
 * 자막은 **그림에서 다시 뽑을 수 있다**(관리자 [글자 읽기] — [[book-ocr-subtitle]]).
 * 그래서 되살리기는 「완전 복구」가 아니라 **다시 올리는 일을 없애 주는 것**이다.
 * 화면이 그렇게 말해야 한다 — 「다 돌아왔어요」라고 하면 운영자가 자막 없는 책을 게시한다.
 */

/** 되살릴 수 있는 책 한 권. */
export interface OrphanBook {
  id: string;
  /** 되살릴 쪽 이름들 — 표지가 있으면 맨 앞. */
  pageIds: string[];
  hasCover: boolean;
  /** 읽어주기 개수(언어 합계). 「무엇이 남아 있는지」를 사람에게 말할 때 쓴다. */
  audioCount: number;
}

/** `book.page.<책>.<쪽>` 에서 쪽 이름만. 아니면 null. */
function pageIdOf(key: string, bookId: string): string | null {
  const m = new RegExp("^book\\.page\\." + bookId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\.(.+)$").exec(key);
  return m ? m[1]! : null;
}

/**
 * 쪽 순서.
 *
 * 🔴 표지가 **맨 앞**이고, 나머지는 `p01·p02…` 의 **숫자 순**이다.
 * 그냥 문자열로 정렬하면 `p10` 이 `p2` 앞에 온다 — 지금은 두 자리로 쓰지만
 * 옛 자료나 손으로 올린 것에 한 자리가 섞일 수 있다([[sort-order-traps]]).
 */
export function sortPageIds(ids: readonly string[]): string[] {
  const num = (s: string) => {
    const m = /(\d+)/.exec(s);
    return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
  };
  return [...ids].sort((a, b) => {
    if (a === "cover") return -1;
    if (b === "cover") return 1;
    const d = num(a) - num(b);
    return d !== 0 ? d : a.localeCompare(b);
  });
}

/**
 * 되살릴 수 있는 책들 — **살아 있는 책 목록을 알 때만** 부른다.
 *
 * 🔴 목록을 못 받았으면 부르지 말 것. 「못 받았다」를 「책이 없다」로 읽으면
 * **멀쩡한 책이 전부 되살리기 후보로** 뜨고, 운영자가 그걸 누르면 지금 책을 덮어쓴다.
 * (`releaseReady` 가 같은 이유로 못 받았을 때 세지 않는다.)
 */
export function orphanBooks(
  assetKeys: readonly string[],
  aliveIds: ReadonlySet<string>,
  bookIdOf: (key: string) => string | null,
): OrphanBook[] {
  const byId = new Map<string, { pages: Set<string>; cover: boolean; audio: number }>();
  for (const key of assetKeys) {
    const id = bookIdOf(key);
    if (!id || aliveIds.has(id)) continue;
    const slot = byId.get(id) ?? { pages: new Set<string>(), cover: false, audio: 0 };
    const page = pageIdOf(key, id);
    if (page) slot.pages.add(page);
    else if (key.startsWith("book.cover.")) slot.cover = true;
    else if (key.startsWith("book.audio.")) slot.audio++;
    byId.set(id, slot);
  }
  return [...byId]
    // 🔴 **쪽 그림이 하나도 없으면 되살리지 않는다.** 소리만 남은 것으로 책을 만들면
    //    빈 쪽만 늘어선 책이 생기고, 그건 「없는 것보다 나쁘다」
    .filter(([, v]) => v.pages.size > 0)
    .map(([id, v]) => ({
      id,
      pageIds: sortPageIds([...v.pages]),
      hasCover: v.cover || v.pages.has("cover"),
      audioCount: v.audio,
    }))
    .sort((a, b) => b.pageIds.length - a.pageIds.length || a.id.localeCompare(b.id));
}

/** 되살린 책의 쪽들. 자막·대체글은 없다 — 그림과 순서만 돌려놓는다. */
export function recoveredPages(o: OrphanBook): { id: string; cover?: true }[] {
  return o.pageIds.map((id) => (id === "cover" ? { id, cover: true as const } : { id }));
}

/**
 * 운영자에게 **무엇이 돌아오고 무엇이 안 돌아오는지** 한 줄로.
 *
 * 🔴 「다 돌아왔어요」라고 하면 자막 없는 책을 그대로 게시한다. 못 돌아오는 것을 먼저 말한다.
 */
export function recoverSay(o: OrphanBook): string {
  const bits = [`${o.pageIds.length}쪽`];
  if (o.hasCover) bits.push("표지");
  if (o.audioCount > 0) bits.push(`읽어주기 ${o.audioCount}개`);
  return `${bits.join(" · ")}가 돌아와요. 자막과 제목은 안 돌아와요 — 되살린 뒤 [글자 읽기]로 자막을 뽑고 제목을 적어 주세요.`;
}
